import { useEffect, useMemo } from 'react';
import { QueryContainer } from '@opensearch-project/opensearch/api/_types/_common.query_dsl';

import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';

import { Bucket, RefinementListItem } from '../types';
import { useOpenSearchContext } from './useOpenSearchContext';
import {
  buildFacetQueryFilters,
  transformBucketToRefinementItem,
} from '../utils';
import { useUpdateSearchParams } from './useUpdateSearchParams';

const useRefinementList = ({ attribute }: { attribute: string }) => {
  const {
    aggregations,
    updateQueryFiltersByFacets,
    queryFiltersByFacets,
    isLoading,
  } = useOpenSearchContext();
  const { updateSearchParams } = useUpdateSearchParams();

  const refinedValues = useMemo(
    () =>
      new Set(
        (
          (queryFiltersByFacets?.[attribute] as QueryContainer)?.bool
            ?.should as QueryContainer[]
        )?.map(query => query.match?.[attribute]),
      ),
    [queryFiltersByFacets],
  );

  const refinementListItems: RefinementListItem[] = useMemo(
    () =>
      aggregations?.facets?.[attribute]?.values?.buckets?.map(
        (bucket: Bucket) => ({
          ...transformBucketToRefinementItem(bucket),
          isRefined:
            refinedValues.has(bucket.key) ||
            (!!bucket.key_as_string &&
              refinedValues.has(JSON.parse(bucket.key_as_string))),
        }),
      ) ?? [],
    [aggregations, refinedValues],
  );

  useEffect(() => {
    if (
      queryFiltersByFacets[attribute] ||
      attribute === PRODUCT_FILTER_ATTRIBUTES._ON_SALE
    ) {
      return;
    }
    // If the attribute is not already in the query
    updateQueryFiltersByFacets((prev: Record<string, QueryContainer>) => ({
      ...prev,
      [attribute]: buildFacetQueryFilters(attribute, []),
    }));
  }, [queryFiltersByFacets]);

  const handleRefine = (value: string | number | boolean) => {
    const newRefined = new Set(refinedValues);
    if (refinedValues.has(value)) {
      newRefined.delete(value);
    } else {
      newRefined.add(value);
    }

    updateSearchParams(attribute, Array.from(newRefined).map(String));

    updateQueryFiltersByFacets((prev: Record<string, QueryContainer>) => ({
      ...prev,
      [attribute]: buildFacetQueryFilters(
        attribute,
        Array.from(newRefined.values()),
      ),
    }));
  };

  const clearAll = () => {
    updateSearchParams(attribute, []);

    updateQueryFiltersByFacets((prev: Record<string, QueryContainer>) => ({
      ...prev,
      [attribute]: buildFacetQueryFilters(attribute, []),
    }));
  };

  return {
    items: refinementListItems,
    refine: handleRefine,
    clearAll,
    isLoading,
  };
};

export { useRefinementList };
